import { combineEpics, ofType } from "redux-observable"
import { concat, of, EMPTY, from, Observable } from "rxjs"
import { flatMap, switchMap, debounceTime } from "rxjs/operators"
import { Epic } from "../../../../epics"

import * as actions from "./actions"
import { RelativeSiblingsResponse } from "../../../../apis/models/relativesiblings"
import { ISmartLocation } from "./index"

const DEFAULT_SIZE = 10

interface CallParameters {
    afterPageIdValue: string | null
    filterValue: string
    sizeValue: number
    location: string
}

const getCallParams = (state: ISmartLocation): CallParameters => ({
    afterPageIdValue: state?.siblings?.lastSiblingId ?? null,
    filterValue: state?.siblings?.filter ?? "",
    sizeValue: state?.siblings?.size ?? DEFAULT_SIZE,
    location: state?.siblings?.location ?? "",
})

const receiveSiblings = (
    action: actions.ReceiveSiblings,
): Observable<actions.ReceiveSiblings | actions.UpdateSiblingsVisibility> => {
    if (!action.Data || !action.Meta) {
        return of(actions.updateSiblingsVisibility(false))
    }

    return concat(
        of(actions.receiveSiblings(action.Data, action.Meta)),
        of(actions.updateSiblingsVisibility(true)),
    )
}

const requestSiblings = (
    state: ISmartLocation,
    callback: (
        location: string,
        filterValue: string,
        sizeValue: number,
        afterPageIdValue: string | null,
    ) => Promise<RelativeSiblingsResponse>,
): Observable<RelativeSiblingsResponse | never> => {
    const params = getCallParams(state)
    return !!params.location
        ? from(
              callback(
                  params.location,
                  params.filterValue,
                  params.sizeValue,
                  params.afterPageIdValue,
              ),
          )
        : EMPTY
}

export const initializeEpic: Epic = (
    action$,
    state$,
    { smartLocation: { relativeSiblingsAPI } },
) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        switchMap(() =>
            requestSiblings(
                state$.value.smartLocation,
                relativeSiblingsAPI.getRelativeSiblings,
            ),
        ),
        flatMap((action: actions.ReceiveSiblings) => receiveSiblings(action)),
    )

export const requestRelativeSiblingsEpic: Epic = (
    action$,
    state$,
    { smartLocation: { relativeSiblingsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_SIBLINGS),
        switchMap(() =>
            requestSiblings(
                state$.value.smartLocation,
                relativeSiblingsAPI.getRelativeSiblings,
            ),
        ),
        flatMap((action: actions.ReceiveSiblings) => receiveSiblings(action)),
    )

export const reorderSiblingsEpic: Epic = (
    action$,
    state$,
    { smartLocation: { relativeSiblingsAPI } },
) =>
    action$.pipe(
        ofType(actions.REORDER_SIBLINGS),
        switchMap((action: actions.ReorderSiblings) => {
            const { targetLocation, reorderedSiblingId } = action
            const params = getCallParams(state$.value.smartLocation)

            return !!reorderedSiblingId && !!params.location
                ? from(
                      relativeSiblingsAPI.reorderRelativeSibling(
                          targetLocation,
                          reorderedSiblingId,
                          params.location,
                          params.filterValue,
                          params.sizeValue,
                      ),
                  )
                : EMPTY
        }),
        flatMap((action: actions.ReceiveSiblings) => receiveSiblings(action)),
    )

export const removeSiblingEpic: Epic = (
    action$,
    state$,
    { smartLocation: { relativeSiblingsAPI } },
) =>
    action$.pipe(
        ofType(actions.REMOVE_SIBLING),
        switchMap((action: actions.RemoveSibling) => {
            const { selectionFilterId } = action
            const params = getCallParams(state$.value.smartLocation)

            return !!selectionFilterId && !!params.location
                ? from(
                      relativeSiblingsAPI.removeRelativeSibling(
                          selectionFilterId,
                          params.location,
                          params.filterValue,
                          params.sizeValue,
                      ),
                  )
                : EMPTY
        }),
        flatMap((action: actions.ReceiveSiblings) => receiveSiblings(action)),
    )

export const filterSiblingsEpic: Epic = (
    action$,
    state$,
    { smartLocation: { relativeSiblingsAPI, filteringDelay } },
) =>
    action$.pipe(
        ofType(actions.FILTER_SIBLINGS),
        debounceTime(filteringDelay),
        switchMap(() =>
            requestSiblings(
                state$.value.smartLocation,
                relativeSiblingsAPI.getRelativeSiblings,
            ),
        ),
        flatMap((action: actions.ReceiveSiblings) => receiveSiblings(action)),
    )

export const loadMoreSiblingsEpic: Epic = (
    action$,
    state$,
    { smartLocation: { relativeSiblingsAPI } },
) =>
    action$.pipe(
        ofType(actions.LOAD_MORE_SIBLINGS),
        switchMap(() =>
            requestSiblings(
                state$.value.smartLocation,
                relativeSiblingsAPI.getRelativeSiblings,
            ),
        ),
        flatMap((action: actions.ReceiveSiblings) => receiveSiblings(action)),
    )

export default combineEpics(
    initializeEpic,
    requestRelativeSiblingsEpic,
    reorderSiblingsEpic,
    removeSiblingEpic,
    filterSiblingsEpic,
    loadMoreSiblingsEpic,
)
